import { Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';

interface QuestionnaireExportPasswordModalProps {
    opened: boolean;
    onClose: () => void;
    onSubmit: (password: string) => void;
    isSubmitting: boolean;
}

interface FormValues {
    password: string;
    confirmPassword: string;
}

export default function QuestionnaireExportPasswordModal({
    opened,
    onClose,
    onSubmit,
    isSubmitting,
}: QuestionnaireExportPasswordModalProps) {
    const { t } = useTranslation();

    const pwRegex = z
        .string()
        .min(8, { message: t('formError.passwordMustBeAtLeast8CharactersLong') })
        .regex(/[a-z]/, { message: t('formError.passwordMustContainLowercase') })
        .regex(/[A-Z]/, { message: t('formError.passwordMustContainUppercase') })
        .regex(/[#!?@$%^&*\-_]/, { message: t('formError.passwordMustContainSpecialCharacter') });

    const schema = z
        .object({
            password: pwRegex,
            confirmPassword: z.string().min(1, { message: t('formError.confirmPasswordIsRequired') }),
        })
        .refine((data) => data.password === data.confirmPassword, {
            path: ['confirmPassword'],
            message: t('formError.passwordsDoNotMatch'),
        });

    const form = useForm<FormValues>({
        initialValues: { password: '', confirmPassword: '' },
        validate: zodResolver(schema),
    });

    const handleSubmit = (values: FormValues) => {
        onSubmit(values.password);
    };

    return (
        <Modal opened={opened} onClose={onClose} title={t('questionnaireExport.passwordModal.title')}>
            <form
                onSubmit={(event) => {
                    // This modal can be rendered inside another component's own
                    // <form> (e.g. HcfForm's clinic edit form). Without stopping
                    // propagation here, submitting this form also bubbles up and
                    // submits that unrelated ambient form.
                    event.stopPropagation();
                    form.onSubmit(handleSubmit)(event);
                }}
            >
                <Stack>
                    <Text size="sm">{t('questionnaireExport.passwordModal.description')}</Text>
                    <PasswordInput
                        label={t('questionnaireExport.passwordModal.passwordLabel')}
                        {...form.getInputProps('password')}
                    />
                    <PasswordInput
                        label={t('questionnaireExport.passwordModal.confirmPasswordLabel')}
                        {...form.getInputProps('confirmPassword')}
                    />
                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={onClose}>
                            {t('button.cancel')}
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            {t('questionnaireExport.downloadButton')}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}
